import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';

const makeLocalKey = () => `row_${Date.now()}_${Math.random().toString(16).slice(2)}`;

const normalizeActive = raw => {
  if (raw === false) return false;
  const s = String(raw ?? '').trim().toLowerCase();
  if (s === 'false' || s === '0' || s === 'no' || s === 'off') return false;
  if (raw === undefined || raw === null || s === '') return true;
  return Boolean(raw);
};

const normalizeNumberOr = (raw, fallback) => {
  if (raw === '' || raw === null || raw === undefined) return fallback;
  const n = Number(raw);
  return Number.isFinite(n) ? n : fallback;
};

const normalizePriceOverride = raw => {
  if (raw === undefined) return undefined;
  if (raw === null) return null;
  const s = String(raw).trim();
  if (s === '') return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};

function UpgradeConfigList({ value, setValue, isEditable, label, fieldSchema, apiOptions }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const optionsKey = fieldSchema?.apiOptionsKey || fieldSchema?.fieldKey;
  const upgradeOptions = useMemo(() => {
    const fromKey = optionsKey ? apiOptions?.[optionsKey] : null;
    const fallback = apiOptions?.includedUpsellIds;
    return Array.isArray(fromKey) ? fromKey : Array.isArray(fallback) ? fallback : [];
  }, [apiOptions, optionsKey]);

  const [rows, setRows] = useState([]);

  useEffect(() => {
    const incoming = Array.isArray(value) ? value : [];
    const normalizedIncoming = incoming.map(entry => ({
        localKey: makeLocalKey(),
        _id: entry?._id,
        upgradeId:
          entry?.upgradeId && typeof entry.upgradeId === 'object'
            ? entry.upgradeId._id || entry.upgradeId.id || entry.upgradeId.value || ''
            : entry?.upgradeId ?? '',
        sortKey: normalizeNumberOr(entry?.sortKey, 0),
        activeInThisProduct: normalizeActive(entry?.activeInThisProduct),
        priceOverride: entry?.priceOverride ?? null,
      }));

    setRows(prev => {
      const pending = (Array.isArray(prev) ? prev : []).filter(r => !r?.upgradeId);
      return [...normalizedIncoming, ...pending];
    });
  }, [JSON.stringify(value)]);

  const emit = useCallback(
    nextRows => {
      const payload = (Array.isArray(nextRows) ? nextRows : [])
        .map(r => {
          const upgradeIdRaw = r?.upgradeId;
          const upgradeId =
            upgradeIdRaw && typeof upgradeIdRaw === 'object'
              ? upgradeIdRaw._id || upgradeIdRaw.id || upgradeIdRaw.value
              : upgradeIdRaw;
          if (!upgradeId) return null;
          const out = {
            upgradeId,
            sortKey: normalizeNumberOr(r.sortKey, 0),
            activeInThisProduct: normalizeActive(r.activeInThisProduct),
            priceOverride: normalizePriceOverride(r.priceOverride),
          };
          if (r._id != null) out._id = r._id;
          return out;
        })
        .filter(Boolean);
      const current = Array.isArray(value) ? value : [];
      if (JSON.stringify(payload) === JSON.stringify(current)) return;
      setValue(payload);
    },
    [setValue, value]
  );

  const updateRow = useCallback(
    (localKey, patch) => {
      setRows(prev => {
        const next = (Array.isArray(prev) ? prev : []).map(r => {
          if (r.localKey !== localKey) return r;
          return { ...r, ...patch };
        });

        const updatedRow = next.find(r => r.localKey === localKey);
        const hadUpgrade = Boolean(
          prev?.find?.(r => r?.localKey === localKey)?.upgradeId
        );
        const hasUpgradeNow = Boolean(updatedRow?.upgradeId);
        const isUpgradeChange = Object.prototype.hasOwnProperty.call(patch || {}, 'upgradeId');

        // Only persist when we have a valid upgrade selection. This prevents "empty" rows from
        // instantly disappearing due to backend validation / payload filtering.
        if (hasUpgradeNow || (isUpgradeChange && hadUpgrade && !hasUpgradeNow)) {
          emit(next);
        }
        return next;
      });
    },
    [emit]
  );

  const addRow = useCallback(() => {
    setRows(prev => {
      return [
        ...(Array.isArray(prev) ? prev : []),
        {
          localKey: makeLocalKey(),
          _id: undefined,
          upgradeId: '',
          sortKey: 0,
          activeInThisProduct: true,
          priceOverride: null,
        },
      ];
    });
  }, []);

  const deleteRow = useCallback(
    localKey => {
      setRows(prev => {
        const previousRow = (Array.isArray(prev) ? prev : []).find(r => r.localKey === localKey);
        const next = (Array.isArray(prev) ? prev : []).filter(r => r.localKey !== localKey);
        if (previousRow?.upgradeId) emit(next);
        return next;
      });
    },
    [emit]
  );

  const renderRow = row => {
    const selectedOption =
      upgradeOptions.find(o => String(o.value) === String(row.upgradeId)) || null;

    const sortKeyDefault = row.sortKey ?? 0;
    const priceOverrideDefault =
      row.priceOverride === null || row.priceOverride === undefined ? '' : String(row.priceOverride);

    const content = (
      <Stack
        direction={isMobile ? 'column' : 'row'}
        spacing={1.5}
        alignItems={isMobile ? 'stretch' : 'center'}
        sx={{ py: 1 }}
      >
        <Autocomplete
          options={upgradeOptions}
          value={selectedOption}
          onChange={(_, opt) => updateRow(row.localKey, { upgradeId: opt ? opt.value : '' })}
          isOptionEqualToValue={(opt, val) => String(opt?.value) === String(val?.value)}
          getOptionLabel={opt => String(opt?.displayLabel || opt?.label || '')}
          renderInput={params => (
            <TextField
              {...params}
              label='Upgrade'
              placeholder='Upgrade auswählen'
              size='small'
              disabled={!isEditable}
            />
          )}
          disabled={!isEditable}
          fullWidth
          sx={{ minWidth: isMobile ? undefined : 320, flex: 1 }}
        />

        <TextField
          label='Sortierung'
          type='number'
          size='small'
          defaultValue={sortKeyDefault}
          disabled={!isEditable}
          onBlur={e => updateRow(row.localKey, { sortKey: normalizeNumberOr(e.target.value, 0) })}
          inputProps={{ step: '1' }}
          sx={{ width: isMobile ? '100%' : 140 }}
        />

        <TextField
          label='Preis-Override'
          type='number'
          size='small'
          defaultValue={priceOverrideDefault}
          disabled={!isEditable}
          onBlur={e => updateRow(row.localKey, { priceOverride: e.target.value })}
          inputProps={{ step: '0.01', min: '0' }}
          sx={{ width: isMobile ? '100%' : 160 }}
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(row.activeInThisProduct)}
              onChange={e => updateRow(row.localKey, { activeInThisProduct: e.target.checked })}
              disabled={!isEditable}
            />
          }
          label='Aktiv'
          sx={{ m: 0, whiteSpace: 'nowrap' }}
        />

        <IconButton
          aria-label='delete'
          onClick={() => deleteRow(row.localKey)}
          disabled={!isEditable}
          size='small'
        >
          <DeleteIcon fontSize='small' />
        </IconButton>
      </Stack>
    );

    if (isMobile) {
      return (
        <Box
          key={row.localKey}
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 1.5,
          }}
        >
          {content}
        </Box>
      );
    }

    return (
      <Box key={row.localKey}>
        {content}
        <Divider />
      </Box>
    );
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ mb: 1 }}>
        <Typography variant='subtitle2' color='text.secondary'>
          {label}
        </Typography>
        <Tooltip title={isEditable ? 'Eintrag hinzufügen' : ''}>
          <span>
            <Button
              variant='outlined'
              size='small'
              startIcon={<AddIcon />}
              onClick={addRow}
              disabled={!isEditable}
            >
              Hinzufügen
            </Button>
          </span>
        </Tooltip>
      </Stack>

      {rows.length === 0 ? (
        <Typography variant='body2' color='text.secondary'>
          Keine Einträge. Über “Hinzufügen” können Sie Upgrades ergänzen.
        </Typography>
      ) : (
        <Box>{rows.map(renderRow)}</Box>
      )}

      <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mt: 1 }}>
        Hinweis: Leerer “Preis-Override” bedeutet Standardpreis des Upgrades.
      </Typography>
    </Box>
  );
}

export default UpgradeConfigList;

